import classNames from 'classnames'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { Skeleton } from 'src/components/Skeleton'
import { AI_LISTENING_PROMPT_DETAILS_ROUTE } from 'src/constants/routes'
import {
  useAiListeningLink,
  useAiListeningPeriodLabel,
} from 'src/hooks/useAiListeningDateRange'
import {
  PromptHighlight,
  PromptsOverview,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './prompts.module.scss'
import { AreaChart } from '../shared/charts/AreaChart'
import { MeterBar } from '../shared/charts/MeterBar'
import { StanceSplit } from '../shared/charts/StanceSplit'
import { ContextTags } from '../shared/ContextTags'
import { DeltaBadge } from '../shared/Dashboard/DeltaBadge'
import { Pill } from '../shared/Pill'
import { formatNumber, formatPercent } from '../shared/utils'

const ChatIcon: FC = () => (
  <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
    <path
      d="M3.5 4h13v9h-8l-5 3.5V4z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.4"
    />
  </svg>
)

const AtIcon: FC = () => (
  <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
    <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M13 10v1.2a2 2 0 0 0 4 0V10a7 7 0 1 0-2.8 5.6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.4"
    />
  </svg>
)

const BarsIcon: FC = () => (
  <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
    <path
      d="M5 15V10M10 15V5M15 15V8"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.6"
    />
  </svg>
)

interface HighlightCardProps {
  highlight: PromptHighlight | null
  variant: 'attention' | 'top'
}

const HighlightCard: FC<HighlightCardProps> = ({ highlight, variant }) => {
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const isTop = variant === 'top'

  return (
    <div className={styles.tile}>
      <Pill size="md" tone={isTop ? 'green' : 'orange'}>
        {isTop ? 'Top Performing Prompt' : 'Needs Attention'}
      </Pill>
      {highlight ? (
        <>
          <p className={styles.highlightPrompt}>„{highlight.prompt}“</p>
          <ContextTags showType={false} tags={highlight.contextTags} />
          <div className={styles.highlightStats}>
            <span>Visibility</span>
            <strong>{formatPercent(highlight.visibility)}</strong>
            {isTop ? (
              <>
                <span>Mentions</span>
                <strong>{formatNumber(highlight.mentions)}</strong>
              </>
            ) : (
              <>
                <span>Negative sentiment</span>
                <strong
                  className={
                    highlight.negativeShare > 0 ? styles.negative : undefined
                  }
                >
                  {formatPercent(highlight.negativeShare, 1)}
                </strong>
              </>
            )}
          </div>
          <button
            className={styles.outlineButton}
            onClick={() =>
              navigate(
                link(AI_LISTENING_PROMPT_DETAILS_ROUTE, {
                  promptId: highlight._id,
                })
              )
            }
            type="button"
          >
            View Prompt Details
          </button>
        </>
      ) : (
        <p className={styles.tileEmpty}>
          Not enough answers in this period to pick a prompt yet.
        </p>
      )}
    </div>
  )
}

interface PerformanceHeaderProps {
  isLoading: boolean
  overview?: PromptsOverview
}

// "Prompt performance": how the AI assistants answer the tracked prompts in
// the selected period.
export const PerformanceHeader: FC<PerformanceHeaderProps> = ({
  isLoading,
  overview,
}) => {
  const periodLabel = useAiListeningPeriodLabel()

  return (
    <section className={styles.performance}>
      <div>
        <h1 className={styles.performanceTitle}>Prompt performance</h1>
        <p className={styles.performanceSubtitle}>
          How AI assistants answer your tracked prompts · {periodLabel}
        </p>
      </div>

      {isLoading || !overview ? (
        <div className={styles.performanceSkeleton}>
          <Skeleton height={190} radius={16} width="100%" />
          <Skeleton height={190} radius={16} width="100%" />
        </div>
      ) : (
        <>
          <div className={styles.performanceTop}>
            <div className={classNames(styles.tile, styles.visibilityTile)}>
              <div className={styles.visibilityHead}>
                <div className={styles.visibilityValueBlock}>
                  <h2 className={styles.tileTitle}>Brand Visibility</h2>
                  <p className={styles.tileSubtitle}>
                    AI answers mention your brand
                  </p>
                  <span className={styles.visibilityValue}>
                    {formatPercent(overview.visibility.value)}
                    <DeltaBadge
                      kind="points"
                      value={overview.visibility.delta}
                    />
                  </span>
                </div>
                <AreaChart
                  className={styles.visibilityTrend}
                  height={44}
                  points={overview.visibility.series}
                  showDates
                />
              </div>
              <StanceSplit compact split={overview.visibility.split} />
            </div>

            <div className={styles.tile}>
              <span className={styles.tileLabel}>
                <span className={styles.tileIcon}>
                  <ChatIcon />
                </span>
                Tracked Prompts
              </span>
              <span className={styles.bigValue}>
                {formatNumber(overview.counts.active)}
                <small>active now</small>
              </span>
              <span className={styles.countPills}>
                <Pill tone="purple">Active {overview.counts.active}</Pill>
                <Pill tone="grey">Draft {overview.counts.draft}</Pill>
                <Pill tone="green">Completed {overview.counts.completed}</Pill>
              </span>
            </div>

            <div className={styles.tile}>
              <span className={styles.tileLabel}>
                <span className={styles.tileIcon}>
                  <AtIcon />
                </span>
                Total Mentions
              </span>
              <span className={styles.bigValue}>
                {formatNumber(overview.totalMentions)}
              </span>
              <span className={styles.tileNote}>
                Avg.{' '}
                <strong>{formatNumber(overview.avgMentionsPerPrompt)}</strong>{' '}
                per prompt
              </span>
              <span className={styles.tileNote}>
                Across {overview.counts.active} active{' '}
                {overview.counts.active === 1 ? 'prompt' : 'prompts'}
              </span>
            </div>
          </div>

          <div className={styles.performanceBottom}>
            <HighlightCard highlight={overview.topPrompt} variant="top" />
            <HighlightCard
              highlight={overview.needsAttention}
              variant="attention"
            />
            <div className={styles.tile}>
              <span className={styles.tileLabel}>
                <span className={styles.tileIcon}>
                  <BarsIcon />
                </span>
                Share of Voice
              </span>
              <p className={styles.tileSubtitle}>
                Who AI assistants recommend across your prompts
              </p>
              {overview.shareOfVoice.length === 0 && (
                <p className={styles.tileEmpty}>No brands named yet.</p>
              )}
              <div className={styles.voiceRows}>
                {overview.shareOfVoice.map((brand) => {
                  const isYou = brand.label === 'you'
                  return (
                    <div className={styles.voiceRow} key={brand.key}>
                      <span className={styles.voiceName}>
                        {isYou ? (
                          <>
                            <strong>{brand.name}</strong> (you)
                          </>
                        ) : (
                          brand.name
                        )}
                      </span>
                      <strong>{formatPercent(brand.share)}</strong>
                      <MeterBar
                        className={styles.voiceBar}
                        color={isYou ? '#8080ff' : '#9ca3af'}
                        value={brand.share}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
